import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { Alert } from '../components/ui/Alert';
import { toast } from 'sonner';
import { apiFetch, parseJsonResponse, isNetworkErrorResponse } from '../utils/api';

interface Product {
  id: number;
  cod_articulo: string;
  referencia: string | null;
  articulo: string;
  precio: number;
  cod_familia: number | null;
  familia: string | null;
  cod_subcategoria: number | null;
  subcategoria: string | null;
  cod_subarticulo: number | null;
  subarticulo_nombre: string | null;
  descatalogado: number;
  mostrar_en_hiomenu: number;
  kcal: number | null;
  description: string | null;
  ingredients: string | null;
  tiene_aguacate: number;
  es_picante: number;
  es_recomendado: number;
  image_path: string | null;
  logo_url: string | null;
  image_url: string | null;
  cod_tarifa_venta: number | null;
  tarifa_venta: string | null;
  is_active: number;
  display_order: number;
  last_sync: string | null;
  created_at: string;
  updated_at: string;
}

interface Tariff {
  id: number;
  establishment_id: number;
  cod_tarifa_hioffice: number;
  tarifa_name: string;
  is_default: number;
  is_active: number;
}

interface Establishment {
  id: number;
  name_establishment: string;
  code_establishment_hioffice: number;
  is_active: number;
}

export const Products: React.FC = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState<Product[]>([]);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [selectedEstablishmentId, setSelectedEstablishmentId] = useState<string>('');
  const [selectedTariffId, setSelectedTariffId] = useState<string>('');
  const [loadingEstablishments, setLoadingEstablishments] = useState(true);
  const [loadingTariffs, setLoadingTariffs] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  
  // Filtros adicionales
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedFamily, setSelectedFamily] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const [filterAguacate, setFilterAguacate] = useState<boolean | null>(null);
  const [filterPicante, setFilterPicante] = useState<boolean | null>(null);
  const [filterRecomendado, setFilterRecomendado] = useState<boolean | null>(null);
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Cargar establecimientos
  useEffect(() => {
    const loadEstablishments = async () => {
      if (!token) {
        setLoadingEstablishments(false);
        return;
      }

      setLoadingEstablishments(true);
      try {
        const params = new URLSearchParams({
          page: '1',
          limit: '1000'
        });

        const route = `establishments/list&${params}`;
        const res = await apiFetch(route, {}, token);
        
        if (isNetworkErrorResponse(res)) {
          setError('Error de conexión al cargar establecimientos');
          return;
        }

        const json = await parseJsonResponse(res);

        if (!res.ok || json.error) {
          const errorMessage = typeof json.error === 'string' ? json.error : json.message || json.data?.error || 'Error al cargar establecimientos';
          throw new Error(errorMessage);
        }

        setEstablishments(json.data?.establishments || []);
      } catch (err: any) {
        console.error('Error cargando establecimientos:', err);
        setError(err.message || 'Error al cargar establecimientos');
      } finally {
        setLoadingEstablishments(false);
      }
    };

    loadEstablishments();
  }, [token]);

  // Cargar tarifas cuando se selecciona un establecimiento
  useEffect(() => {
    const loadTariffs = async () => {
      if (!token || !selectedEstablishmentId) {
        setTariffs([]);
        setSelectedTariffId('');
        setProducts([]);
        return;
      }

      setLoadingTariffs(true);
      try {
        const params = new URLSearchParams({
          establishment_id: selectedEstablishmentId
        });

        const route = `tariffs/list&${params}`;
        const res = await apiFetch(route, {}, token);
        
        if (isNetworkErrorResponse(res)) {
          toast.error('Error de conexión al cargar tarifas');
          return;
        }

        const json = await parseJsonResponse(res);

        if (!res.ok || json.error) {
          const errorMessage = typeof json.error === 'string' ? json.error : json.message || json.data?.error || 'Error al cargar tarifas';
          throw new Error(errorMessage);
        }

        const tariffsData = json.data?.tariffs || [];
        setTariffs(tariffsData);
        
        // Si hay una tarifa, seleccionarla automáticamente
        if (tariffsData.length > 0) {
          setSelectedTariffId(tariffsData[0].cod_tarifa_hioffice.toString());
        } else {
          setSelectedTariffId('');
          setProducts([]);
          toast.warning('Este establecimiento no tiene tarifas configuradas');
        }
      } catch (err: any) {
        console.error('Error cargando tarifas:', err);
        toast.error(err.message || 'Error al cargar tarifas');
        setTariffs([]);
        setSelectedTariffId('');
        setProducts([]);
      } finally {
        setLoadingTariffs(false);
      }
    };

    loadTariffs();
  }, [token, selectedEstablishmentId]);

  // Función para cargar productos
  const loadProducts = async (showToast: boolean = true) => {
    if (!token || !selectedEstablishmentId || !selectedTariffId) {
      setProducts([]);
      return;
    }

    setLoadingProducts(true);
    try {
      const params = new URLSearchParams({
        establishment_id: selectedEstablishmentId,
        cod_tarifa_venta: selectedTariffId
      });

      const route = `products/list&${params}`;
      const res = await apiFetch(route, {}, token);
      
      if (isNetworkErrorResponse(res)) {
        if (showToast) {
          toast.error('Error de conexión al cargar productos');
        }
        return;
      }

      const json = await parseJsonResponse(res);

      if (!res.ok || json.error) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || json.data?.error || 'Error al cargar productos';
        throw new Error(errorMessage);
      }

      setProducts(json.data?.products || []);
      
      if (showToast) {
        const total = json.data?.total || 0;
        if (total === 0) {
          toast.info('No se encontraron productos para esta configuración');
        } else {
          toast.success(`${total} producto(s) encontrado(s)`);
        }
      }
    } catch (err: any) {
      console.error('Error cargando productos:', err);
      if (showToast) {
        toast.error(err.message || 'Error al cargar productos');
      }
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Cargar productos cuando se selecciona un establecimiento y una tarifa
  useEffect(() => {
    loadProducts();
  }, [token, selectedEstablishmentId, selectedTariffId]);

  const handleSync = async () => {
    if (!token || !selectedEstablishmentId) {
      toast.error('Debes seleccionar un establecimiento');
      return;
    }
    
    setSyncing(true);
    try {
      console.log('🔄 Iniciando sincronización de productos...', {
        establishmentId: selectedEstablishmentId
      });
      
      const res = await apiFetch(
        'products/sync',
        {
          method: 'POST',
          body: JSON.stringify({
            establishment_id: parseInt(selectedEstablishmentId, 10)
          })
        },
        token
      );
      
      console.log('📡 Respuesta HTTP recibida:', {
        status: res.status,
        ok: res.ok,
        statusText: res.statusText
      });
      
      if (isNetworkErrorResponse(res)) {
        console.error('❌ Error de red al sincronizar');
        toast.error('Error de conexión al sincronizar');
        return;
      }
      
      // Leer el texto crudo antes de parsear para debugging
      const text = await res.text();
      console.log('📄 Respuesta cruda (primeros 500 caracteres):', text.substring(0, 500));
      
      // Intentar parsear JSON
      let json;
      try {
        json = JSON.parse(text);
        console.log('✅ JSON parseado correctamente');
      } catch (parseError: any) {
        console.error('❌ Error parseando JSON:', parseError);
        console.error('📄 Texto completo recibido:', text);
        toast.error('Error: La respuesta del servidor no es JSON válido. Revisa la consola para más detalles.');
        return;
      }
      
      // Log completo de la respuesta
      console.log('📦 Respuesta completa de sincronización:', {
        status: res.status,
        ok: res.ok,
        json: json,
        data: json.data,
        success: json.success,
        error: json.error
      });
      
      if (json.error || !json.success) {
        let errorMessage = 'Error al sincronizar productos';
        if (json.error) {
          errorMessage = typeof json.error === 'string' ? json.error : String(json.error);
        } else if (json.message) {
          errorMessage = typeof json.message === 'string' ? json.message : String(json.message);
        }
        
        console.error('❌ Error en sincronización:', {
          error: json.error,
          message: json.message,
          data: json.data,
          errorMessage
        });
        
        toast.error(errorMessage);
      } else {
        const data = json.data || {};
        const loginSuccess = data.loginSuccess ?? false;
        const exportSuccess = data.exportSuccess ?? false;
        
        console.log('✅ Respuesta exitosa:', {
          loginSuccess,
          exportSuccess,
          hasExportData: !!data.export_data,
          exportError: data.exportError
        });
        
        if (!loginSuccess) {
          toast.error('Error en la autenticación con Hioffice');
        } else if (!exportSuccess) {
          const exportError = data.exportError || 'Error desconocido en exportación';
          toast.error(`Error en exportación: ${exportError}`);
        } else {
          const synced = data.synced || 0;
          const updated = data.updated || 0;
          const deleted = data.deleted || 0;
          const total = data.total || 0;
          
          let message = `Sincronización exitosa: ${synced} nuevo(s), ${updated} actualizado(s)`;
          if (deleted > 0) {
            message += `, ${deleted} eliminado(s)`;
          }
          message += `, ${total} total`;
          
          toast.success(message, {
            duration: Infinity,
            action: {
              label: 'Enterado',
              onClick: () => {}
            }
          });
          
          // Recargar productos si hay tarifa seleccionada
          if (selectedTariffId) {
            // Recargar productos sin mostrar toast adicional (ya mostramos el de sincronización)
            loadProducts(false);
          }
        }
      }
    } catch (err: any) {
      console.error('❌ Error sincronizando productos:', err);
      console.error('Stack trace:', err.stack);
      toast.error(err.message || 'Error al sincronizar productos');
    } finally {
      setSyncing(false);
    }
  };

  // Obtener familias y subcategorías únicas de los productos
  const getUniqueFamilies = (): string[] => {
    const families = new Set<string>();
    products.forEach(product => {
      if (product.familia) {
        families.add(product.familia);
      }
    });
    return Array.from(families).sort();
  };

  const getUniqueSubcategories = (): string[] => {
    const subcategories = new Set<string>();
    products.forEach(product => {
      if (product.subcategoria) {
        subcategories.add(product.subcategoria);
      }
    });
    return Array.from(subcategories).sort();
  };

  // Aplicar filtros a los productos
  const getFilteredProducts = (): Product[] => {
    return products.filter(product => {
      // Filtro de búsqueda por nombre
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesName = product.articulo.toLowerCase().includes(searchLower);
        const matchesSubarticulo = product.subarticulo_nombre?.toLowerCase().includes(searchLower);
        const matchesDescription = product.description?.toLowerCase().includes(searchLower);
        if (!matchesName && !matchesSubarticulo && !matchesDescription) {
          return false;
        }
      }

      // Filtro por familia
      if (selectedFamily && product.familia !== selectedFamily) {
        return false;
      }

      // Filtro por subcategoría
      if (selectedSubcategory && product.subcategoria !== selectedSubcategory) {
        return false;
      }

      // Filtro por aguacate
      if (filterAguacate !== null) {
        if (filterAguacate && product.tiene_aguacate !== 1) {
          return false;
        }
        if (!filterAguacate && product.tiene_aguacate === 1) {
          return false;
        }
      }

      // Filtro por picante
      if (filterPicante !== null) {
        if (filterPicante && product.es_picante !== 1) {
          return false;
        }
        if (!filterPicante && product.es_picante === 1) {
          return false;
        }
      }

      // Filtro por recomendado
      if (filterRecomendado !== null) {
        if (filterRecomendado && product.es_recomendado !== 1) {
          return false;
        }
        if (!filterRecomendado && product.es_recomendado === 1) {
          return false;
        }
      }

      return true;
    });
  };

  const filteredProducts = getFilteredProducts();

  // Limpiar filtros
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedFamily('');
    setSelectedSubcategory('');
    setFilterAguacate(null);
    setFilterPicante(null);
    setFilterRecomendado(null);
  };

  const hasActiveFilters = searchTerm || selectedFamily || selectedSubcategory || 
    filterAguacate !== null || filterPicante !== null || filterRecomendado !== null;

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Math.round(price));
  };

  const getFamilyBadgeColor = (familia: string | null): "success" | "error" | "warning" | "info" | "neutral" | undefined => {
    if (!familia) return 'neutral';
    const colors: Record<string, "success" | "error" | "warning" | "info" | "neutral"> = {
      'Bebidas': 'info',
      'Comidas': 'success',
      'Postres': 'warning',
      'Extras': 'neutral'
    };
    return colors[familia] || 'neutral';
  };

  return (
    <DashboardLayout
      title="Gestión de Productos"
      subtitle="Visualiza y gestiona los productos de los establecimientos"
      showBackButton={true}
      backPath="/management"
    >
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Filtros principales */}
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <Select
                    label="Establecimiento"
                    value={selectedEstablishmentId}
                    onChange={(e) => {
                      setSelectedEstablishmentId(e.target.value);
                      setSelectedTariffId('');
                      setProducts([]);
                      clearFilters();
                    }}
                    options={[
                      { value: '', label: '-- Selecciona un establecimiento --' },
                      ...establishments.filter(est => est.is_active === 1).map((est) => ({
                        value: est.id.toString(),
                        label: `${est.name_establishment} (Cód: ${est.code_establishment_hioffice})`
                      }))
                    ]}
                    disabled={loadingEstablishments}
                  />
                </div>
                <div>
                  <Select
                    label="Tarifa"
                    value={selectedTariffId}
                    onChange={(e) => setSelectedTariffId(e.target.value)}
                    options={[
                      { value: '', label: '-- Selecciona una tarifa --' },
                      ...tariffs.map((tariff) => ({
                        value: tariff.cod_tarifa_hioffice.toString(),
                        label: `${tariff.tarifa_name}${tariff.is_default === 1 ? ' (Predeterminada)' : ''}`
                      }))
                    ]}
                    disabled={!selectedEstablishmentId || loadingTariffs}
                  />
                </div>
              </div>

              {/* Filtros automáticos visibles */}
              {selectedEstablishmentId && selectedTariffId && (
                <div className="mb-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-gray-700">Filtros automáticos aplicados:</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="success">✓ No descatalogados</Badge>
                    <Badge variant="success">✓ Mostrar en HioMenu activo</Badge>
                    {selectedTariffId && (
                      <Badge variant="info">
                        Tarifa: {tariffs.find(t => t.cod_tarifa_hioffice.toString() === selectedTariffId)?.tarifa_name || selectedTariffId}
                      </Badge>
                    )}
                  </div>
                </div>
              )}

              {/* Filtros adicionales */}
              {selectedEstablishmentId && selectedTariffId && products.length > 0 && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-2"
                    >
                      {showFilters ? '▼' : '▶'} Filtros adicionales
                    </button>
                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        Limpiar filtros
                      </button>
                    )}
                  </div>

                  {showFilters && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                      {/* Búsqueda por nombre */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Buscar producto
                        </label>
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Nombre, descripción..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      {/* Filtro por familia */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Familia
                        </label>
                        <select
                          value={selectedFamily}
                          onChange={(e) => setSelectedFamily(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Todas las familias</option>
                          {getUniqueFamilies().map((family) => (
                            <option key={family} value={family}>
                              {family}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Filtro por subcategoría */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Subcategoría
                        </label>
                        <select
                          value={selectedSubcategory}
                          onChange={(e) => setSelectedSubcategory(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Todas las subcategorías</option>
                          {getUniqueSubcategories().map((subcategory) => (
                            <option key={subcategory} value={subcategory}>
                              {subcategory}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Filtros por características */}
                      <div className="md:col-span-2 lg:col-span-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Características
                        </label>
                        <div className="flex flex-wrap gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filterAguacate === true}
                              onChange={(e) => setFilterAguacate(e.target.checked ? true : null)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">🥑 Con aguacate</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filterPicante === true}
                              onChange={(e) => setFilterPicante(e.target.checked ? true : null)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">🌶️ Picante</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={filterRecomendado === true}
                              onChange={(e) => setFilterRecomendado(e.target.checked ? true : null)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-700">⭐ Recomendado</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Indicador de filtros activos */}
                  {hasActiveFilters && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="text-xs text-gray-600">Filtros activos:</span>
                      {searchTerm && (
                        <Badge variant="info" className="text-xs">
                          Buscar: "{searchTerm}"
                        </Badge>
                      )}
                      {selectedFamily && (
                        <Badge variant="info" className="text-xs">
                          Familia: {selectedFamily}
                        </Badge>
                      )}
                      {selectedSubcategory && (
                        <Badge variant="info" className="text-xs">
                          Subcategoría: {selectedSubcategory}
                        </Badge>
                      )}
                      {filterAguacate === true && (
                        <Badge variant="success" className="text-xs">🥑 Con aguacate</Badge>
                      )}
                      {filterPicante === true && (
                        <Badge variant="error" className="text-xs">🌶️ Picante</Badge>
                      )}
                      {filterRecomendado === true && (
                        <Badge variant="warning" className="text-xs">⭐ Recomendado</Badge>
                      )}
                    </div>
                  )}
                </div>
              )}

              {selectedEstablishmentId && (
                <div className="flex justify-end mt-4">
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleSync}
                    disabled={syncing || loadingEstablishments}
                  >
                    {syncing ? 'Sincronizando...' : 'Sincronizar desde Hioffice'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Mensaje de error */}
          {error && (
            <Alert variant="error" className="mb-6">
              {error}
            </Alert>
          )}

          {/* Lista de productos */}
          {loadingProducts ? (
            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-gray-500">Cargando productos...</p>
              </CardContent>
            </Card>
          ) : products.length === 0 && selectedEstablishmentId && selectedTariffId ? (
            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-gray-500">No se encontraron productos para esta configuración.</p>
                <p className="text-sm text-gray-400 mt-2">
                  Asegúrate de que los productos estén activos, no descatalogados y tengan "mostrar en HioMenu" habilitado.
                </p>
              </CardContent>
            </Card>
          ) : products.length > 0 ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-800">
                  Productos {hasActiveFilters ? `(${filteredProducts.length} de ${products.length})` : `(${products.length})`}
                </h2>
              </div>
              {hasActiveFilters && filteredProducts.length === 0 && (
                <Alert variant="warning" className="mb-4">
                  No se encontraron productos que coincidan con los filtros aplicados.
                </Alert>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredProducts.map((product) => (
                  <Card key={product.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-3 mb-3">
                        {product.image_url && (
                          <div className="flex-shrink-0">
                            <img
                              src={product.image_url}
                              alt={product.articulo}
                              className="w-20 h-20 object-cover rounded-lg"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-semibold text-gray-800 mb-1">
                            {product.articulo}
                          </h3>
                          {product.subarticulo_nombre && (
                            <p className="text-sm text-gray-600 mb-2">
                              {product.subarticulo_nombre}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Precio:</span>
                          <span className="text-lg font-bold text-green-600">
                            {formatPrice(product.precio)}
                          </span>
                        </div>
                        {product.familia && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Familia:</span>
                            <Badge variant={getFamilyBadgeColor(product.familia)}>
                              {product.familia}
                            </Badge>
                          </div>
                        )}
                        {product.subcategoria && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Subcategoría:</span>
                            <span className="text-sm text-gray-800">{product.subcategoria}</span>
                          </div>
                        )}
                        {product.kcal && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Calorías:</span>
                            <span className="text-sm text-gray-800">{product.kcal} kcal</span>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 mb-3">
                        {product.tiene_aguacate === 1 && (
                          <Badge variant="success">🥑 Aguacate</Badge>
                        )}
                        {product.es_picante === 1 && (
                          <Badge variant="error">🌶️ Picante</Badge>
                        )}
                        {product.es_recomendado === 1 && (
                          <Badge variant="warning">⭐ Recomendado</Badge>
                        )}
                      </div>

                      {product.description && (
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                          {product.description}
                        </p>
                      )}

                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>Código: {product.cod_articulo}</span>
                          {product.referencia && (
                            <span>Ref: {product.referencia}</span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <Card>
              <CardContent className="p-6 text-center">
                <p className="text-gray-500">Selecciona un establecimiento y una tarifa para ver los productos.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

